/**
 * ESS Header Component - Simplified and unified
 * Removes InternetHeader/IntranetHeader complexity from Volto Light Theme
 */
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Container from '../../../../components/Atoms/Grid/Container';
import MobileNavigation from '../MobileNavigation/MobileNavigation';
import { useIntl, defineMessages } from 'react-intl';
import config from '@plone/volto/registry';
import cx from 'classnames';
import {
  Anontools,
  LanguageSelector,
  Logo,
  Navigation,
  UniversalLink,
} from '@plone/volto/components';
import '@plone/components/dist/basic.css';
import { PageSection, ContentWrapper } from '../../../../components/Layout';
import './Header.scss';
import './HeaderMobile.scss';

// Hook for hide on scroll behavior
const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = useState('up');
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection('up');
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return scrollDirection;
};

const messages = defineMessages({
  siteLabel: {
    id: 'siteLabel',
    defaultMessage: ' ',
  },
});

// Level 1: Default header configuration (addon fallback)
const defaultHeaderConfig = {
  // Visibility toggles
  showLanguageSelector: true,
  showToolsWrapper: true,
  showAnontools: true,
  showNavigation: true,
  showMobileNavigation: true,
  showLogo: true,
  showSiteActions: true,
  
  // Content
  siteLabel: null, // Optional site label text
    
  // Styling options
  headerBackgroundColor: null, // null = use CSS variable
  headerTextColor: null,       // null = use CSS variable
  toolsWrapperBackground: null,
  
  // Behavior
  stickyHeader: false,         // Make header sticky on scroll
  hideHeaderOnScroll: false,   // Hide header when scrolling down
};

const Header = (props) => {
  const { pathname } = props;
  const { settings } = config;
  const intl = useIntl();
  
  // Level 3: Backend configuration (highest priority)
  const backendHeaderData = useSelector(
    (state) => state.content?.data?.headerData,
  );
  
  // Level 2: Project configuration from config.settings
  const projectHeaderConfig = settings.headerConfig || {};
  
  // Merge configurations: Backend > Project > Defaults
  const headerConfig = {
    ...defaultHeaderConfig,
    ...projectHeaderConfig,
    ...backendHeaderData,
  };
  
  // Hook for hide on scroll behavior
  const scrollDirection = useScrollDirection();
  
  const token = useSelector((state) => state.userSession.token);
  const siteAction = useSelector(
    (state) => state.content.data?.['@components']?.actions?.site_actions,
  );
  
  // Site label handling
  const translatedSiteLabel = intl.formatMessage(messages.siteLabel);
  let siteLabel = headerConfig.siteLabel || settings.siteLabel;
  if (translatedSiteLabel && translatedSiteLabel !== 'siteLabel' && translatedSiteLabel !== ' ') {
    siteLabel = translatedSiteLabel;
  }

  const containerStyles = {
    position: headerConfig.stickyHeader ? 'sticky' : undefined,
    top: headerConfig.stickyHeader ? 0 : undefined,
    zIndex: headerConfig.stickyHeader ? 1000 : undefined,
    ...(props.style || {}),
  };

  return (
    <header
      className={cx('header-wrapper', { 
        'sticky-header': headerConfig.stickyHeader,
        'hide-on-scroll': headerConfig.hideHeaderOnScroll,
        'scrolled-down': headerConfig.hideHeaderOnScroll && scrollDirection === 'down',
        'scrolled-up': headerConfig.hideHeaderOnScroll && scrollDirection === 'up',
      })}
    >
      <PageSection padding={false} style = {{
        backgroundColor: 'var(--header-statedefault-bgcolor)',
        borderBottom: "var(--header-statedefault-border)",
      }}>
        <ContentWrapper 
          style={containerStyles}
          className="header"
          {...props}
        >

              {/* Tools Wrapper (Language, Login, Site Actions, Label) */}
              {headerConfig.showToolsWrapper && (
                <div 
                  className="tools-wrapper" 
                  style={{
                    backgroundColor: headerConfig.toolsWrapperBackground || undefined,
                  }}
                >
                  {headerConfig.showLanguageSelector && (
                    <div className="language-selector-wrapper">
                      <LanguageSelector />
                    </div>
                  )}

                  <div className="tools">
                    {!token && headerConfig.showAnontools && <Anontools />}
                    {headerConfig.showSiteActions && siteAction &&
                      siteAction.map((item) => (
                        <UniversalLink key={item.url} href={item.url}>
                          {item.title}
                        </UniversalLink>
                      ))}
                  </div>
                  
                  {siteLabel && (
                    <div className="site-label">
                      <p>{siteLabel}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Main Header: Logo, Navigation, Search */}
              <div className="logo-nav-wrapper">
                {headerConfig.showLogo && (
                  <div className="logo">
                    <Logo />
                  </div>
                )}
                
                {headerConfig.showMobileNavigation && (
                  <div className="mobile-nav-wrapper">
                    <MobileNavigation pathname={pathname} />
                  </div>
                )}
                
                {headerConfig.showNavigation && <Navigation pathname={pathname} />}
              </div>
  
        </ContentWrapper>
      </PageSection>
    </header>
  );
};

Header.propTypes = {
  showToolsWrapper: PropTypes.bool,
  token: PropTypes.string,
  pathname: PropTypes.string.isRequired,
  siteLabel: PropTypes.string,
  siteAction: PropTypes.arrayOf(PropTypes.object),
  style: PropTypes.object,
};

Header.defaultProps = {
  token: null,
};

export default Header;
